import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  IconButton, 
  Menu, 
  MenuItem, 
  Divider, 
  Badge,
  useTheme,
  alpha,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Tooltip,
  SwipeableDrawer,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Route as RouteIcon,
  DirectionsBus,
  ListAlt,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings,
  NotificationsNone,
  AccountCircle,
  Logout,
  SatelliteAlt,
  GpsFixed,
  Wifi,
  ExpandMore,
  Fullscreen,
  FullscreenExit,
  Home,
  Map,
  Timeline,
  Settings as SettingsIcon,
  Person,
  Help,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout as logoutApi } from '../../services/api';
import { clearAuth } from '../../services/auth';

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [fullScreenMode, setFullScreenMode] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      await logoutApi();
    } catch (error) {
      // Clear local auth even if the server session has already expired.
    } finally {
      clearAuth();
      navigate('/login', { replace: true });
    }
  };

  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullScreenMode(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreenMode(false);
      }
    }
  };

  const navItems = [
    { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { label: 'Routes', icon: <RouteIcon />, path: '/routes' },
    { label: 'Vehicles', icon: <DirectionsBus />, path: '/vehicles' },
    { label: 'Logs', icon: <ListAlt />, path: '/logs' },
    { label: 'Profile', icon: <Person />, path: '/profile' },
    { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { label: 'Help', icon: <Help />, path: '/help' },
  ];

  const bottomNavItems = [
    { label: 'Home', icon: <Home />, path: '/' },
    { label: 'Routes', icon: <Map />, path: '/routes' },
    { label: 'Vehicles', icon: <DirectionsBus />, path: '/vehicles' },
    { label: 'Logs', icon: <Timeline />, path: '/logs' },
    { label: 'Profile', icon: <Person />, path: '/profile' },
    { label: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { label: 'Help', icon: <Help />, path: '/help' },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getActiveIndex = () => {
    const index = bottomNavItems.findIndex(item => item.path === location.pathname);
    return index !== -1 ? index : 0;
  };

  const handleBottomNavChange = (event, newValue) => {
    const path = bottomNavItems[newValue]?.path;
    if (path) {
      window.location.href = path;
    }
  };

  const renderMobileDrawer = () => (
    <SwipeableDrawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      onOpen={() => setMobileMenuOpen(true)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          bgcolor: alpha(theme.palette.background.default, 0.98),
          backdropFilter: 'blur(10px)',
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }
      }}
    >
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.2) }}>
              <SatelliteAlt sx={{ fontSize: 22, color: '#00e676' }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#00e676', fontSize: '1rem' }}>
                GPS Tracker
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: '0.6rem' }}>
                v1.0.0
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Navigation Items */}
        <List sx={{ flex: 1 }}>
          {navItems.map((item) => (
            <ListItem 
              button 
              key={item.label}
              component={Link}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: isActive(item.path) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
              }}
            >
              <ListItemIcon sx={{ 
                color: isActive(item.path) ? '#00e676' : theme.palette.text.secondary,
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: isActive(item.path) ? 700 : 500,
                    color: isActive(item.path) ? '#00e676' : theme.palette.text.primary,
                    fontSize: '0.9rem'
                  }
                }}
              />
              {isActive(item.path) && (
                <Box sx={{ width: 4, height: 28, bgcolor: '#00e676', borderRadius: 2 }} />
              )}
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ my: 2 }} />
        
        {/* System Status */}
        <Box sx={{ px: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            System Status
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                icon={<Wifi sx={{ fontSize: 14 }} />}
                label="Connected"
                size="small"
                sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: '#00e676', fontWeight: 600 }}
              />
              <Chip
                icon={<GpsFixed sx={{ fontSize: 14 }} />}
                label="Active"
                size="small"
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 600 }}
              />
            </Box>
          </Box>
        </Box>
        
        {/* User Section */}
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.2) }}>
              <AccountCircle sx={{ color: '#00e676' }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Admin User</Typography>
              <Typography variant="caption" color="text.secondary">admin@gpstracker.com</Typography>
            </Box>
            <IconButton size="small" component={Link} to="/settings" sx={{ color: theme.palette.text.secondary }}>
              <Settings sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </SwipeableDrawer>
  );

  const renderDesktopNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {navItems.map((item) => (
        <Button
          key={item.label}
          component={Link}
          to={item.path}
          startIcon={item.icon}
          sx={{
            color: isActive(item.path) ? '#00e676' : theme.palette.text.primary,
            fontWeight: isActive(item.path) ? 700 : 500,
            textTransform: 'none',
            px: { sm: 1.5, md: 2 },
            py: 1,
            borderRadius: 2,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              color: '#00e676'
            },
            '&::after': isActive(item.path) ? {
              content: '""',
              position: 'absolute',
              bottom: 4,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 20,
              height: 3,
              bgcolor: '#00e676',
              borderRadius: 2
            } : {}
          }}
        >
          {item.label}
        </Button>
      ))}
    </Box>
  );

  const renderMobileNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {navItems.map((item) => (
        <Tooltip title={item.label} key={item.label}>
          <IconButton
            component={Link}
            to={item.path}
            sx={{
              color: isActive(item.path) ? '#00e676' : theme.palette.text.secondary,
              bgcolor: isActive(item.path) ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
              borderRadius: 2,
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
              '&::after': isActive(item.path) ? {
                content: '""',
                position: 'absolute',
                bottom: 2,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 16,
                height: 2,
                bgcolor: '#00e676',
                borderRadius: 2
              } : {}
            }}
          >
            {item.icon}
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: alpha(theme.palette.background.paper, 0.85),
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64, md: 72 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 0.5, color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1.5 }, flexShrink: 0 }}>
            <Avatar sx={{ 
              width: { xs: 28, sm: 32, md: 40 },
              height: { xs: 28, sm: 32, md: 40 },
              bgcolor: alpha(theme.palette.primary.main, 0.15)
            }}>
              <SatelliteAlt sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, color: '#00e676' }} />
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#00e676', fontSize: { sm: '0.9rem', md: '1.1rem' } }}>
                GPS Tracker
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Navigation */}
          {!isMobile && (isTablet ? renderMobileNav() : renderDesktopNav())}

          <Box sx={{ flexGrow: 0.5 }} />

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5, md: 1 } }}>
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton onClick={handleNotificationsOpen} sx={{ color: theme.palette.text.secondary }}>
                <Badge badgeContent={3} color="error" variant="dot">
                  <NotificationsNone />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Full Screen */}
            {!isMobile && (
              <Tooltip title={fullScreenMode ? 'Exit Full Screen' : 'Full Screen'}>
                <IconButton onClick={toggleFullScreen} sx={{ color: theme.palette.text.secondary }}>
                  {fullScreenMode ? <FullscreenExit /> : <Fullscreen />}
                </IconButton>
              </Tooltip>
            )}

            {/* Profile Menu */}
            <Button
              onClick={handleMenuOpen}
              endIcon={!isMobile && <ExpandMore />}
              sx={{
                color: theme.palette.text.primary,
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2,
                px: { xs: 0.5, sm: 1, md: 1.5 },
                py: 0.5,
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
              }}
            >
              <Avatar sx={{ 
                width: { xs: 24, sm: 28, md: 32 },
                height: { xs: 24, sm: 28, md: 32 },
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                mr: { xs: 0, sm: 0.5, md: 1 }
              }}>
                <AccountCircle sx={{ color: '#00e676', fontSize: { xs: 16, sm: 18, md: 22 } }} />
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Admin
              </Typography>
            </Button>

            {/* Profile Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: 2,
                  mt: 1,
                  bgcolor: alpha(theme.palette.background.paper, 0.98),
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  minWidth: { xs: 180, sm: 200 }
                }
              }}
            >
              <MenuItem onClick={handleMenuClose} component={Link} to="/profile">
                <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} component={Link} to="/settings">
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMenuClose} component={Link} to="/help">
                <ListItemIcon><Help fontSize="small" /></ListItemIcon>
                <ListItemText>Help</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
                <ListItemIcon><Logout fontSize="small" sx={{ color: theme.palette.error.main }} /></ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationsAnchorEl}
          open={Boolean(notificationsAnchorEl)}
          onClose={handleNotificationsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              mt: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              minWidth: { xs: 280, sm: 320 }
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notifications</Typography>
          </Box>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {[1, 2, 3].map((item) => (
              <MenuItem key={item} sx={{ py: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item === 1 ? 'Simulation Started' : item === 2 ? 'New Vehicle Added' : 'Route Updated'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item === 1 ? 'Route K.S. Depo to Bhanjpura' : item === 2 ? 'Vehicle RJ-01-AB-1234' : 'City Center route modified'}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Box>
          <Box sx={{ p: 1, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button size="small" fullWidth sx={{ textTransform: 'none', color: theme.palette.primary.main, fontWeight: 600 }}>
              View All Notifications
            </Button>
          </Box>
        </Menu>
      </AppBar>

      {/* Mobile Drawer */}
      {renderMobileDrawer()}

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            zIndex: 1000,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            display: { xs: 'block', sm: 'none' }
          }} 
          elevation={3}
        >
          <BottomNavigation
            showLabels
            value={getActiveIndex()}
            onChange={handleBottomNavChange}
            sx={{
              height: 56,
              '& .MuiBottomNavigationAction-root': {
                color: theme.palette.text.secondary,
                minWidth: 'auto',
                padding: '4px 0',
                '&.Mui-selected': { color: '#00e676' },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.55rem',
                  '&.Mui-selected': { fontSize: '0.65rem' }
                },
                '& .MuiSvgIcon-root': { fontSize: 22 }
              }
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.label}
                label={item.label}
                icon={item.icon}
                value={item.path}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </>
  );
};

export default Navbar;